import {Injectable, ReflectiveInjector} from '@angular/core';
import {
  BaseRequestOptions,
  BaseResponseOptions,
  BrowserXhr,
  ConnectionBackend,
  CookieXSRFStrategy,
  Headers,
  Http,
  RequestOptions,
  ResponseOptions,
  XHRBackend,
  XSRFStrategy
} from '@angular/http';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {Observable} from 'rxjs/Observable';
import {HttpService} from './http.service';
import {environment} from '../../../environments/environment';
import {httpFactory} from './http.factory';
import {LoaderService} from './loader.service';
import {UtilService} from './util.service';

function checkUser(control: FormControl, source: any) {
  // Manually inject Http
  const httpService = ReflectiveInjector.resolveAndCreate([
    Http, BrowserXhr,
    {provide: ConnectionBackend, useClass: XHRBackend},
    {provide: ResponseOptions, useClass: BaseResponseOptions},
    {provide: XSRFStrategy, useFactory: () => new CookieXSRFStrategy},
    {provide: RequestOptions, useClass: BaseRequestOptions},
    {provide: HttpService, useFactory: httpFactory, deps: [XHRBackend, RequestOptions, LoaderService]}
  ]).get(Http);
  return new Observable((obs: any) => {
    const options = new RequestOptions();
    options.headers = new Headers();

    const Mytoken = localStorage.getItem('loggedInUser') ? JSON.parse(localStorage.getItem('loggedInUser')).token : null;

    if (Mytoken) {
      options.headers.append('Content-Type', 'application/json');
      options.headers.append('X-Auth-Token', Mytoken);
    } else {
      options.headers.append('Content-Type', 'application/x-www-form-urlencoded');
    }
    const api_url = source === 'brand' ? `/brand/checkBrandName` : `/checkEmail`;
    control
      .valueChanges
      .debounceTime(400)
      .flatMap(value => httpService.post(`${environment.BASE_API}${environment.ROLE}${api_url}`, value, options))
      .subscribe(
        (data: any) => {
          const errorStatus = data.json().error;
          let reason;
          if (errorStatus === 1) {
            if (source === 'brand') {
              reason = 'brandTaken';
            } else if (source === 'admin') {
              reason = 'adminTaken';
            }
            obs.next({[reason]: true});
            obs.complete();
          } else {
            obs.next(null);
            obs.complete();
          }
        },
        error => {
          const errorStatus = error.json().error;
          let reason;
          if (errorStatus === 1) {
            if (source === 'brand') {
              reason = 'brandTaken';
            } else if (source === 'admin') {
              reason = 'adminTaken';
            }
          }
          obs.next({[reason]: true});
          obs.complete();
        }
      );
  });
}

@Injectable()
export class ValidationService {
  email_regexPattern = '[a-zA-Z0-9.-_]{1,}@[a-zA-Z.-]{2,}[.]{1}[a-zA-Z]{2,}';
  password_regexPattern = '^(?=.*?[A-Z])(?=.*?[#?!@$%^&*-]).{8,}$';
  phoneNumber_regexPattern = '^[0-9]*$';
  webUrl_regexPattern = 'https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9]\.[^\s]{2,}';
  isNumber_regexPattern = '[0-9]*$';
  isNumber2Decimal_regexPattern = '[0-9]+(\.[0-9][0-9]?)?';
  isNumber2DecimalWithMinus_regexPattern = '^-?[0-9]+(\.[0-9][0-9]?)?';

  constructor(
    private http: HttpService,
    private utilService: UtilService,
    private httpService: Http
  ) {
  }

  minLengthArray(min: number) {
    return (c: AbstractControl): { [key: string]: any } => {
      if (c.value.length >= min) {
        return null;
      }
      return {'minLengthArray': {valid: false}};
    };
  }

  maxLength(max: number) {
    return (c: AbstractControl): { [key: string]: boolean } => {
      if (c.value && c.value.toString().length > max) {
        return {'maxlength': true};
      }
      return null;
    };
  }

  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value !== '' && control.value != null
      && control.value !== undefined) ? (control.value || '').trim().length === 0 : false;
    const isValid = !isWhitespace;
    return isValid ? null : {'whitespace': true};
  }

  checkName = (field: string, caseSensitive: boolean = false): ValidatorFn => {
    return (formArray: FormArray): { [key: string]: boolean } => {
      return null;
    };
  }

  uniqueBy = (field: string, caseSensitive: boolean = false): ValidatorFn => {
    return (formArray: FormArray): { [key: string]: boolean } => {
      const controls: AbstractControl[] = formArray.controls.filter((formGroup: FormGroup) => {
        return this.utilService.isPresent(formGroup.get(field).value);
      });
      const uniqueObj: any = {uniqueBy: true};
      let find = false;

      if (controls.length) {
        controls.map(formGroup => formGroup.get(field)).forEach(x => {
          if (x.errors) {
            delete x.errors['uniqueBy'];
            if (this.utilService.isBlank(x.errors)) {
              x.setErrors(null);
            }
          }
        });
        for (let i = 0; i < controls.length; i++) {
          const formGroup: FormGroup = controls[i] as FormGroup;
          const mainControl: AbstractControl = formGroup.get(field);
          const val: string = mainControl.value;

          const mainValue: string = caseSensitive ? val : val.toLowerCase();
          controls.forEach((group: FormGroup, index: number) => {
            if (i === index) {
              return;
            }

            const currControl: any = group.get(field);
            const tempValue: string = currControl.value;
            const currValue: string = caseSensitive ? tempValue : tempValue.toLowerCase();
            let newErrors: any;

            if (mainValue === currValue) {
              if (this.utilService.isBlank(currControl.errors)) {
                newErrors = uniqueObj;
              } else {
                newErrors = Object.assign(currControl.errors, uniqueObj);
              }
              currControl.setErrors(newErrors);
              find = true;
            }
          });
        }

        if (find) {
          return uniqueObj;
        }
      }

      return null;
    };
  }

  checkDateRange(c: any) {
    const dateRangeError: any = {dateRangeError: true};
    let range = false;
    if (c && c.value && c.value.length) {
      c.controls.forEach(x => {
        if (x.errors && x.errors['dateRangeError']) {
          delete x.errors['dateRangeError'];
          if (this.utilService.isBlank(x.errors)) {
            x.setErrors(null);
          }
        } else if (this.utilService.isBlank(x.errors)) {
          x.setErrors(null);
        }
      });
      for (let i = 0; i < c.value.length; i++) {
        if (c.value[i].dateFrom && c.value[i].dateFrom.formatted && c.value[i].dateTo && c.value[i].dateTo.formatted) {
          if (new Date(c.value[i].dateTo.formatted) < new Date(c.value[i].dateFrom.formatted)) {
            c.controls[i].setErrors(dateRangeError);
            range = true;
          }
        }
      }
      if (range) {
        return dateRangeError;
      }
    }
    return null;
  }

  dateValidator(c: any) {
    const datesOverlapped: any = {datesOverlapped: true};
    let find = false;
    if (c && c.value && c.value.length) {
      c.controls.forEach(x => {
        if (x.errors && x.errors['datesOverlapped']) {
          delete x.errors['datesOverlapped'];
          if (this.utilService.isBlank(x.errors)) {
            x.setErrors(null);
          }
        } else if (this.utilService.isBlank(x.errors)) {
          x.setErrors(null);
        }
      });
      for (let i = 0; i < c.value.length - 1; i += 1) {
        for (let j = i + 1; j < c.value.length; j += 1) {
          if (c.value[i].dateFrom && c.value[i].dateTo && c.value[j].dateFrom && c.value[j].dateTo) {
            const isDateRangeOverlaps = this.utilService.dateRangeOverlaps(new Date(c.value[i].dateFrom.formatted),
              new Date(c.value[i].dateTo.formatted), new Date(c.value[j].dateFrom.formatted), new Date(c.value[j].dateTo.formatted));
            if (isDateRangeOverlaps) {
              c.controls[i].setErrors(datesOverlapped);
              c.controls[j].setErrors(datesOverlapped);
              find = true;
            }
          }
        }
      }
      if (find) {
        return datesOverlapped;
      }
      return null;
    }
    return null;
  }

  simpleCheckDateRange(c: any) {
    if (!c.parent) {
      return;
    }
    const form = c.parent;
    const dateRangeError: any = {dateRangeError: true};
    let range = false;
    Object.keys(form.controls).forEach(x => {
      if (form.controls[x].errors && form.controls[x].errors['dateRangeError']) {
        delete form.controls[x].errors['dateRangeError'];
        if (this.utilService.isBlank(form.controls[x].errors)) {
          form.controls[x].setErrors(null);
        }
      } else if (this.utilService.isBlank(form.controls[x].errors)) {
        form.controls[x].setErrors(null);
      }
    });
    if (form.controls.dateFrom.value && form.controls.dateFrom.value.formatted && form.controls.dateTo.value
      && form.controls.dateTo.value.formatted) {
      if (new Date(form.controls.dateTo.value.formatted) < new Date(form.controls.dateFrom.value.formatted)) {
        // form.controls.dateTo.setErrors(dateRangeError);
        range = true;
      }
    }
    if (range) {
      return dateRangeError;
    } else {
      return null;
    }
  }

  validateCheckboxes(boxes: FormControl) {
    let valid = false,
      k: any;

    for (k in boxes.value) {
      const val = boxes.value[k];
      if (val) {
        valid = true;
        break;
      }
    }

    if (valid) {
      return null;
    }
    return {checkboxRequired: true};
  }

  dateLessThan(from: string, to: string) {
    return (group: FormGroup): { [key: string]: any } => {
      const f = group.controls[from];
      const t = group.controls[to];
      if (f.value && t.value) {
        if (new Date(f.value).setHours(0, 0, 0, 0) > new Date(t.value).setHours(0, 0, 0, 0)) {
          return {
            //  dates: true
            dates: `End Date should be greater than Start Date`
          };
        }
      }
      return {};
    };
  }

  matchPassword(group: FormGroup) {
    const password = group.controls['password_new'].value;
    const confirmPassword = group.controls['password_confirm'].value;

    if (password !== confirmPassword) {
      return {matchPassword: true};
    }
  }

  checkUserName(control: FormControl) {
    return checkUser(control, 'brand');
  }

  checkEmail(control: FormControl) {
    return checkUser(control, 'admin');
  }
}




// WEBPACK FOOTER //
// ./src/app/shared/_services/validation.service.ts